import React, { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { GenderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import UpdateShimmer from "../../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import CountryCitySelector from "../../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import CustomerGroupSelector from "../../../components/CustomComponents/DropdownSelections/CustomerGroupSelector";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import { ThreeDropdownBirthdatePicker } from "../../../components/CustomComponents/DatePicker/DatePicker";
import { useSelector } from "react-redux";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast.js";
import "./updateCustomer.css";
import { floorLoyaltyPoints } from "../../../utils/loyaltyPoints";
import {
  formatDialCode,
  toE164Phone,
  toLocalPhoneForInput,
} from "../../../utils/phoneUtils";
import { Panel, PanelBody } from "../../../components/panel/panel";
import SaveActions from "../../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../../hooks/useUnsavedChangesGuard.js";

const PREFERENCE_TOGGLES = [
  { name: "Cust_IsCommunicationViaEmail", label: "Via Email" },
  { name: "Cust_IsCommunicationViaText", label: "Via Text" },
  { name: "Cust_IsCommunicationViaPhone", label: "Via Phone" },
  { name: "Cust_Opt_In_Marketing", label: "Marketing Opt-in" },
  { name: "Cust_EnableLoyalty", label: "Enable Loyalty" },
];

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

const UpdateCustomer = () => {
  const { cusID } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [compCurrency, setCompCurrency] = useState("GBP"); // Added state for CompCurrency
  const [customerData, setCustomerData] = useState({});
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);
  const [loyaltyHistoryLoading, setLoyaltyHistoryLoading] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [countryCallCodeMap, setCountryCallCodeMap] = useState({});
  const [companyCountry, setCompanyCountry] = useState("");
  const [companyCallCode, setCompanyCallCode] = useState("");
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    const dob = customerData.Cust_DOB;
    let dobStr = null;
    if (dob instanceof Date && !isNaN(dob.getTime())) {
      dobStr = `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, "0")}-${String(dob.getDate()).padStart(2, "0")}`;
    } else if (typeof dob === "string" && dob) {
      dobStr = dob;
    }
    return JSON.stringify({
      Cust_Fname: customerData.Cust_Fname || "",
      Cust_Lname: customerData.Cust_Lname || "",
      Cust_Email: customerData.Cust_Email || "",
      Cust_Mobile: customerData.Cust_Mobile || "",
      Cust_Phone: customerData.Cust_Phone || "",
      Cust_Address: customerData.Cust_Address || "",
      Cust_HouseNo: customerData.Cust_HouseNo || "",
      Cust_PostCode: customerData.Cust_PostCode || "",
      Cust_Country: customerData.Cust_Country || "",
      Cust_City: selectedCity || "",
      Cust_Gender: customerData.Cust_Gender || "",
      Cust_DOB: dobStr,
      Cust_Note: customerData.Cust_Note || "",
      Cust_GroupID: selectedGroup || null,
      Cust_IsCommunicationViaEmail: Boolean(customerData.Cust_IsCommunicationViaEmail),
      Cust_IsCommunicationViaText: Boolean(customerData.Cust_IsCommunicationViaText),
      Cust_IsCommunicationViaPhone: Boolean(customerData.Cust_IsCommunicationViaPhone),
      Cust_Opt_In_Marketing: Boolean(customerData.Cust_Opt_In_Marketing),
      Cust_EnableLoyalty: Boolean(customerData.Cust_EnableLoyalty),
      Cust_Reference_ID: customerData.Cust_Reference_ID || "",
      Cust_VAT_Number: customerData.Cust_VAT_Number || "",
      Cust_BusinessName: customerData.Cust_BusinessName || "",
      Cust_Title: customerData.Cust_Title || "",
      Cust_MaxCredit: customerData.Cust_MaxCredit ?? 0,
    });
  }, [customerData, selectedCity, selectedGroup]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  /** Child dropdowns must not toggle page `loading` — avoids flashing the main content loader. */
  const suppressChildPreloader = useCallback(() => {}, []);

  const companyDialCode = formatDialCode(
    companyCallCode ||
      countryCallCodeMap[companyCountry] ||
      countryCallCodeMap[(companyCountry || "").toUpperCase()] ||
      ""
  );

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };


  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data.data;
      setCompCurrency(profileData?.compCurrency || "GBP");
      setCompanyCountry(profileData?.compCountry || profileData?.CompCountry || "");
      setCompanyCallCode(profileData?.callCode || profileData?.CallCode || "");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  const fetchCountryCallCodes = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COUNTRIES);
      const list = response.data?.data || response.data || [];
      const map = {};
      (Array.isArray(list) ? list : []).forEach((country) => {
        const callCode = country.callCode ?? country.CallCode ?? "";
        const code = country.code ?? country.Code ?? "";
        const code2 = country.code2 ?? country.Code2 ?? "";
        const name = country.name ?? country.Name ?? "";
        if (code) map[code] = callCode;
        if (code2) map[code2] = callCode;
        if (name) map[name] = callCode;
      });
      setCountryCallCodeMap(map);
    } catch {
      // Non-blocking
    }
  }, []);

  const fetchCustomerDetails = useCallback(async () => {
    if (!cusID || !compID) {
      showErrorToast("Invalid customer ID or company ID");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMER_BY_ID, {
        params: { CompID: compID, Cust_Id: cusID },
      });

      const data = response.data.data || response.data;

      // Parse DOB as date-only to avoid timezone shifting the day when API returns UTC midnight
      const parseDOB = (value) => {
        if (!value) return null;
        const str = typeof value === "string" ? value : value.toISOString?.() || "";
        const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          const [, y, m, d] = match;
          return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        }
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      };

      const loyaltyEnabled = toBoolean(
        data.cust_EnableLoyalty ??
        data.Cust_EnableLoyalty ??
        data.enableLoyalty ??
        data.EnableLoyalty ??
        data.isLoyaltyEnabled ??
        data.IsLoyaltyEnabled
      );

      setCustomerData({
        ...data,
        Cust_Id: parseInt(cusID),
        Cust_Guid: data.cust_Guid ?? data.Cust_Guid ?? "",
        CompID: compID,
        Cust_DOB: parseDOB(data.cust_DOB),
        Cust_datetime: data.cust_datetime
          ? new Date(data.cust_datetime)
          : new Date(),
        Cust_Email: data.cust_Email || "",
        Cust_Fname: data.cust_Fname || "",
        Cust_Lname: data.cust_Lname || "",
        Cust_Phone: data.cust_Phone || "",
        Cust_Address: data.cust_Address || "",
        Cust_HouseNo: data.cust_HouseNo || "",
        Cust_PostCode: data.cust_PostCode || "",
        Cust_Mobile: String(
          data.cust_Mobile ?? data.Cust_Mobile ?? ""
        ).trim(),
        Reference_ID: data.reference_ID || "",
        VAT_Number: data.vAT_Number || "",
        Cust_Country: data.cust_Country || "",
        Cust_City: data.cust_City ? String(data.cust_City) : "",
        Cust_Gender: data.cust_Gender || "Male",
        Cust_IsCommunicationViaEmail:
          data.cust_IsCommunicationViaEmail || false,
        Cust_IsCommunicationViaText: data.cust_IsCommunicationViaText || false,
        Cust_IsCommunicationViaPhone:
          data.cust_IsCommunicationViaPhone || false,
        Cust_Opt_In_Marketing: data.cust_Opt_In_Marketing || false,
        Cust_Reference_ID: data.cust_Reference_ID || "",
        Cust_VAT_Number: data.cust_VAT_Number || "",
        Cust_LastOrderType: data.cust_LastOrderType || "",
        Cust_AverageOrderCount: data.cust_AverageOrderCount || 0,
        Cust_AverageOrderSpent: data.cust_AverageOrderSpent || 0,
        Cust_EnableLoyalty: loyaltyEnabled,
        Cust_Deposit: data.cust_Deposit || 0,
        Cus_LastOrderDate: data.cus_LastOrderDate || null,
        Cust_TotalOrderCount: data.cust_TotalOrderCount || 0,
        Cust_TotalOrderSpent: data.cust_TotalOrderSpent || 0,
        Cust_TotalEarnedLoyaltyPoints: data.cust_TotalEarnedLoyaltyPoints || 0,
        Cust_TotalSpentLoyaltyPoints: data.cust_TotalSpentLoyaltyPoints || 0,
        Cust_LoyaltyPoints:
          data.cust_LoyaltyPoints ??
          data.Cust_LoyaltyPoints ??
          0,
        Cust_MaxCredit: data.cust_MaxCredit || 0,
        Cust_CurrentBalance: data.cust_CurrentBalance || 0,
        Cust_GroupID: data.cust_GroupID || null,
        Cust_Note: data.cus_Note || "",
      });

      setSelectedGroup(data.cust_GroupID || null);
      setSelectedCity(data.cust_City ? String(data.cust_City) : null);
    } catch (error) {
      showErrorToast("Failed to load customer details");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [cusID, compID]);

  const fetchCustomerLoyaltyHistory = useCallback(async (custGuid) => {
    const normalizedGuid = (custGuid || "").toString().trim();
    if (!normalizedGuid || !compID) return;

    try {
      setLoyaltyHistoryLoading(true);
      const response = await axiosInstance.get(
        `${APIs.GET_CUSTOMER_LOYALTY_STATUS}/${normalizedGuid}`,
        {
          params: { compID },
        }
      );

      const statusPayload = response.data?.data || response.data?.Data || {};
      const transactions =
        statusPayload?.recentTransactions ||
        statusPayload?.RecentTransactions ||
        [];
      const normalizedTransactions = Array.isArray(transactions) ? transactions : [];
      const visibleTransactions = normalizedTransactions.filter((tx) => {
        const earned = Number(tx?.pointsEarned ?? tx?.PointsEarned ?? 0);
        const spent = Number(tx?.pointsSpent ?? tx?.PointsSpent ?? 0);
        return !(earned === 0 && spent === 0);
      });
      setLoyaltyHistory(visibleTransactions);
    } catch (error) {
      setLoyaltyHistory([]);
      showErrorToast("Failed to load customer loyalty history");
    } finally {
      setLoyaltyHistoryLoading(false);
    }
  }, [compID]);

  const fetchCustomerOrders = useCallback(async (custGuid) => {
    const normalizedGuid = (custGuid || "").toString().trim();
    if (!normalizedGuid || !compID) return;

    try {
      setCustomerOrdersLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMER_ORDERS, {
        params: {
          CompID: compID,
          Cust_Guid: normalizedGuid,
        },
      });

      const orders = response.data?.data || response.data?.Data || [];
      setCustomerOrders(Array.isArray(orders) ? orders : []);
      setOrdersLoaded(true);
    } catch (error) {
      setCustomerOrders([]);
      showErrorToast("Failed to load customer orders");
    } finally {
      setCustomerOrdersLoading(false);
    }
  }, [compID]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setCustomerData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  // Handle toggle switch changes
  const handleToggleChange = useCallback((checked, name) => {
    setCustomerData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle dropdown and special input changes
  const handleSpecialInputChange = useCallback((name, value) => {
    setCustomerData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  }, []);

  // Handle birthdate change
  const handleBirthdateChange = useCallback((birthdateInfo) => {
    setCustomerData((prev) => ({
      ...prev,
      Cust_DOB: birthdateInfo?.formattedDate ?? null,
    }));
  }, []);


  const submitCustomer = async (submitData, exitAfterSave) => {
    try {
    
      setLoading(true);
      submitData.CompID = compID;
      submitData.Cust_Id = parseInt(cusID);
      submitData.cus_Note = submitData.Cust_Note;

      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_CUSTOMER,
        submitData
      );

      if (response.data.status === true) {
        showSuccessToast(
          response.data.message || "Customer Successfully Updated."
        );
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          navigate("/customer/manageCustomer");
        }
        return true;
      }
      showErrorToast(response.data.message || "Error Updating Customer");
      return false;
    } catch (err) {
      if (err.response) {
        showErrorToast(
          err.response.data.message || "Error submitting customer"
        );
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in customer submission");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!customerData.Cust_Fname?.trim()) {
      showErrorToast("Please provide a valid first name");
      return false;
    }

    const emailTrimmed = (customerData.Cust_Email || "").trim();
    if (emailTrimmed && !validateEmail(emailTrimmed)) {
      showErrorToast("Please provide a valid email");
      return false;
    }

    if (customerData.Cust_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return false;
    }

    if (!selectedGroup) {
      showErrorToast("Please select a customer group");
      return false;
    }

    const mobileRaw = (customerData.Cust_Mobile || "").trim();
    let mobileE164 = "";
    if (mobileRaw) {
      if (!companyDialCode && !mobileRaw.startsWith("+")) {
        showErrorToast(
          "Company dial code is not available. Enter mobile in E.164 format, e.g. +447911123456."
        );
        return false;
      }
      mobileE164 = toE164Phone(mobileRaw, companyDialCode);
      if (!mobileE164.startsWith("+") || mobileE164.length < 8) {
        showErrorToast(
          "Please enter a valid mobile number (digits only; country code is added automatically)."
        );
        return false;
      }
    }

    const submitData = {
      ...customerData,
      Cust_Mobile: mobileE164,
      Cust_Email: emailTrimmed,
      Cust_DOB: customerData.Cust_DOB || null,
      Cust_datetime: new Date().toISOString(),
      Cust_City: selectedCity || "",
      Cust_GroupID: selectedGroup,
      cus_Note: customerData.Cust_Note,
    };

    return submitCustomer(submitData, exitAfterSave);
  };


  useEffect(() => {
    fetchCompCurrency(); // Fetch currency on component mount
    fetchCountryCallCodes();
    fetchCustomerDetails();
  }, [fetchCompCurrency, fetchCountryCallCodes, fetchCustomerDetails]);

  // Baseline snapshot after load; refresh once when dial code localizes mobile
  const dialBaselineDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoading) return;
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [initialLoading, getFormSnapshot]);

  useEffect(() => {
    if (initialLoading || !companyDialCode || dialBaselineDoneRef.current) return;
    dialBaselineDoneRef.current = true;
    const id = requestAnimationFrame(() => {
      initialFormSnapshotRef.current = getFormSnapshot();
    });
    return () => cancelAnimationFrame(id);
  }, [companyDialCode, initialLoading, getFormSnapshot]);

  // Once dial code is known, show only the local part in the mobile input.
  useEffect(() => {
    if (!companyDialCode) return;
    setCustomerData((prev) => {
      const current = (prev.Cust_Mobile || "").toString().trim();
      if (!current) return prev;
      const dialDigits = companyDialCode.slice(1);
      const digits = current.replace(/\D/g, "");
      const hasPrefix =
        current.startsWith(companyDialCode) ||
        current.startsWith("+") ||
        (dialDigits && digits.startsWith(dialDigits));
      if (!hasPrefix) return prev;
      const local = toLocalPhoneForInput(current, companyDialCode);
      if (!local || local === current) return prev;
      return { ...prev, Cust_Mobile: local };
    });
  }, [companyDialCode, customerData.Cust_Mobile]);

  useEffect(() => {
    setCustomerOrders([]);
    setOrdersLoaded(false);
    setLoyaltyHistory([]);
  }, [cusID, compID]);

  useEffect(() => {
    const custGuid = (customerData.Cust_Guid || "").toString().trim();
    if (activeTab === "orders" && !ordersLoaded && custGuid) {
      fetchCustomerOrders(custGuid);
    }
  }, [activeTab, ordersLoaded, customerData.Cust_Guid, fetchCustomerOrders]);

  useEffect(() => {
    const custGuid = (customerData.Cust_Guid || "").toString().trim();
    const hasAnyLoyaltyData =
      Boolean(customerData.Cust_EnableLoyalty) ||
      Number(customerData.Cust_LoyaltyPoints || 0) > 0 ||
      Number(customerData.Cust_TotalEarnedLoyaltyPoints || 0) > 0 ||
      Number(customerData.Cust_TotalSpentLoyaltyPoints || 0) > 0;

    if (!custGuid || !hasAnyLoyaltyData) {
      setLoyaltyHistory([]);
      return;
    }

    fetchCustomerLoyaltyHistory(custGuid);
  }, [
    customerData.Cust_Guid,
    customerData.Cust_EnableLoyalty,
    customerData.Cust_LoyaltyPoints,
    customerData.Cust_TotalEarnedLoyaltyPoints,
    customerData.Cust_TotalSpentLoyaltyPoints,
    fetchCustomerLoyaltyHistory,
  ]);

  const RequiredLabel = ({ text }) => (
    <label className="form-label">
      {text} <span className="text-danger">*</span>
    </label>
  );

  const NormalLabel = ({ text }) => (
    <label className="form-label">{text}</label>
  );

  const hasAnyLoyaltyPoints =
    Number(customerData.Cust_LoyaltyPoints || 0) > 0 ||
    Number(customerData.Cust_TotalEarnedLoyaltyPoints || 0) > 0 ||
    Number(customerData.Cust_TotalSpentLoyaltyPoints || 0) > 0;
  const showLoyaltyTab =
    Boolean(customerData.Cust_EnableLoyalty) ||
    hasAnyLoyaltyPoints ||
    loyaltyHistory.length > 0;
  const customerDisplayName = [customerData.Cust_Fname, customerData.Cust_Lname]
    .filter(Boolean)
    .join(" ")
    .trim();

  const getPointsLabel = (transaction) => {
    const earned = Number(transaction?.pointsEarned ?? transaction?.PointsEarned ?? 0);
    const spent = Number(transaction?.pointsSpent ?? transaction?.PointsSpent ?? 0);
    if (earned > 0) return `+${floorLoyaltyPoints(earned)}`;
    if (spent > 0) return `-${floorLoyaltyPoints(spent)}`;
    return "0";
  };

  const getTransactionType = (transaction) =>
    transaction?.transactionType || transaction?.TransactionType || "N/A";

  const getTransactionDate = (transaction) => {
    const dateValue = transaction?.transactionDate || transaction?.TransactionDate;
    if (!dateValue) return "—";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleString();
  };

  const getOrderDate = (order) => {
    const dateValue =
      order?.recvorder_datetime ||
      order?.Recvorder_datetime ||
      order?.recvOrder_datetime ||
      order?.orderDate ||
      order?.OrderDate;
    if (!dateValue) return "—";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleString();
  };

  const getPaymentStatusLabel = (order) => {
    const status = (order?.recvorder_Paymentrecvstatus || order?.Recvorder_Paymentrecvstatus || order?.paymentStatus || "").toString().trim().toLowerCase();
    if (status === "completed") return "Paid";
    if (status === "pending") return "Unpaid";
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : "N/A";
  };

  const getOrderStatusLabel = (order) =>
    (order?.recvorder_status || order?.Recvorder_status || order?.orderStatus || "N/A").toString();

  const formatCurrencyAmount = (value) => {
    const numberValue = Number(value || 0);
    if (!Number.isFinite(numberValue)) return `${getCurrencySymbol(compCurrency)}0.00`;
    return `${getCurrencySymbol(compCurrency)}${numberValue.toFixed(2)}`;
  };

  const sortedLoyaltyTransactions = [...loyaltyHistory].sort((a, b) => {
    const dateA = new Date(a?.transactionDate || a?.TransactionDate || 0).getTime();
    const dateB = new Date(b?.transactionDate || b?.TransactionDate || 0).getTime();
    return dateB - dateA;
  });

  const loyaltySummaryCards = [
    {
      key: "orders",
      label: "Total Orders",
      value: Number(customerData.Cust_TotalOrderCount || 0),
    },
    {
      key: "avgOrder",
      label: "Avg. Order",
      value: formatCurrencyAmount(customerData.Cust_AverageOrderSpent),
    },
    {
      key: "totalSpent",
      label: "Total Spent",
      value: formatCurrencyAmount(customerData.Cust_TotalOrderSpent),
    },
    {
      key: "remaining",
      label: "Remaining Points",
      value: floorLoyaltyPoints(customerData.Cust_LoyaltyPoints || 0),
      valueClassName: "is-remaining",
    },
    {
      key: "spent",
      label: "Spent Points",
      value: floorLoyaltyPoints(customerData.Cust_TotalSpentLoyaltyPoints || 0),
      valueClassName: "is-spent",
    },
  ];

  const sectionStyle = {
    borderBottom: `2px solid ${themeColor || "var(--bs-border-color)"}`,
  };

  const submitStyle = themeColor
    ? {
        backgroundColor: themeColor,
        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
        boxShadow: `0 2px 8px ${themeColor}40`,
      }
    : undefined;

  const tabUnderline = themeColor || "#0d6efd";

  const formatStat = (value, formatter) => {
    if (value === null || value === undefined || value === "") return "—";
    return formatter ? formatter(value) : value;
  };

  const renderGeneralForm = () => (
    <>
      <h6 className="customer-section-heading" style={sectionStyle}>
        Personal
      </h6>
      <div className="row g-3">
        <div className="col-md-6">
          <RequiredLabel text="First Name" />
          <FormInput
            name="Cust_Fname"
            value={customerData.Cust_Fname}
            onchange={handleInputChange}
            placeholder="Enter First Name"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="Last Name" />
          <FormInput
            name="Cust_Lname"
            value={customerData.Cust_Lname}
            onchange={handleInputChange}
            placeholder="Enter Last Name"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="Email" />
          <FormInput
            name="Cust_Email"
            type="email"
            value={customerData.Cust_Email}
            onchange={handleInputChange}
            placeholder="Enter Email"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="Mobile Number" />
          <FormInput
            name="Cust_Mobile"
            placeHolder={
              companyDialCode
                ? "Enter number without country code"
                : "Enter Mobile Number e.g. +447911123456"
            }
            type="text"
            value={customerData.Cust_Mobile ?? ""}
            onchange={handleInputChange}
            prepend={companyDialCode || undefined}
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="Date of Birth" />
          <ThreeDropdownBirthdatePicker
            onDateChange={handleBirthdateChange}
            initialDate={customerData.Cust_DOB}
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="Gender" />
          <GenderDropDownSelection
            selectedValue={customerData.Cust_Gender}
            onChange={(option) =>
              handleSpecialInputChange("Cust_Gender", option?.value)
            }
          />
        </div>
        <div className="col-md-6">
          <RequiredLabel text="Customer Group" />
          <CustomerGroupSelector
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            onChange={(value) => setSelectedGroup(value)}
            setLoading={suppressChildPreloader}
          />
        </div>
      </div>

      <h6 className="customer-section-heading" style={sectionStyle}>
        Address
      </h6>
      <div className="row g-3">
        <div className="col-md-8">
          <NormalLabel text="Address" />
          <FormInput
            name="Cust_Address"
            value={customerData.Cust_Address}
            onchange={handleInputChange}
            placeholder="Enter Address"
          />
        </div>
        <div className="col-md-4">
          <NormalLabel text="House Number" />
          <FormInput
            name="Cust_HouseNo"
            value={customerData.Cust_HouseNo}
            onchange={handleInputChange}
            placeholder="House No."
          />
        </div>
        <div className="col-12">
          <CountryCitySelector
            selectedCountry={customerData.Cust_Country}
            selectedCity={selectedCity}
            setSelectedCity={setSelectedCity}
            onChange={(data) =>
              setCustomerData((prev) => ({ ...prev, ...data }))
            }
            setLoading={suppressChildPreloader}
            isDarkMode={isDarkMode}
            selectedValueIsLabel={true}
            countryFieldName="Cust_Country"
            cityFieldName="Cust_City"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="Post Code" />
          <FormInput
            name="Cust_PostCode"
            value={customerData.Cust_PostCode}
            onchange={handleInputChange}
            placeholder="Enter Post Code"
          />
        </div>
      </div>

      <h6 className="customer-section-heading" style={sectionStyle}>
        Preferences
      </h6>
      <div className="customer-pref-row mb-2">
        {PREFERENCE_TOGGLES.map((item) => (
          <ToggleSwitch
            key={item.name}
            isChecked={customerData[item.name] || false}
            onToggle={(checked) => handleToggleChange(checked, item.name)}
            name={item.name}
            label={item.label}
          />
        ))}
      </div>

      <h6 className="customer-section-heading" style={sectionStyle}>
        Additional
      </h6>
      <div className="row g-3">
        <div className="col-md-6">
          <NormalLabel text="Reference ID" />
          <FormInput
            name="Cust_Reference_ID"
            value={customerData.Cust_Reference_ID}
            onchange={handleInputChange}
            placeholder="Enter Reference ID"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text="VAT Number" />
          <FormInput
            name="Cust_VAT_Number"
            value={customerData.Cust_VAT_Number}
            onchange={handleInputChange}
            placeholder="Enter VAT Number"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel text={`Deposit (${getCurrencySymbol(compCurrency)})`} />
          <FormInput
            name="Cust_Deposit"
            value={customerData.Cust_Deposit}
            onchange={handleInputChange}
            placeholder={`${getCurrencySymbol(compCurrency)}0.00`}
            type="number"
          />
        </div>
        <div className="col-md-6">
          <NormalLabel
            text={`Current Balance (${getCurrencySymbol(compCurrency)})`}
          />
          <FormInput
            name="Cust_CurrentBalance"
            value={customerData.Cust_CurrentBalance}
            onchange={handleInputChange}
            placeholder={`${getCurrencySymbol(compCurrency)}0.00`}
            type="number"
          />
        </div>
        <div className="col-12">
          <NormalLabel text="Note" />
          <FormInput
            name="Cust_Note"
            value={customerData.Cust_Note}
            onchange={handleInputChange}
            placeholder="Enter Note"
          />
        </div>
      </div>

      <h6 className="customer-section-heading" style={sectionStyle}>
        Order Statistics{" "}
        <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>
          (view only)
        </span>
      </h6>
      <div className="table-responsive">
        <table className="table table-sm customer-stats-table mb-0">
          <tbody>
            <tr>
              <td>Total Orders</td>
              <td>{formatStat(customerData.Cust_TotalOrderCount)}</td>
            </tr>
            <tr>
              <td>Total Spent</td>
              <td>
                {formatStat(customerData.Cust_TotalOrderSpent, (v) =>
                  formatCurrencyAmount(v)
                )}
              </td>
            </tr>
            <tr>
              <td>Average Order</td>
              <td>
                {formatStat(customerData.Cust_AverageOrderSpent, (v) =>
                  formatCurrencyAmount(v)
                )}
              </td>
            </tr>
            <tr>
              <td>Loyalty Points</td>
              <td>
                {formatStat(customerData.Cust_LoyaltyPoints, (v) =>
                  floorLoyaltyPoints(v)
                )}
              </td>
            </tr>
            <tr>
              <td>Earned Points</td>
              <td>
                {formatStat(customerData.Cust_TotalEarnedLoyaltyPoints, (v) =>
                  floorLoyaltyPoints(v)
                )}
              </td>
            </tr>
            <tr>
              <td>Spent Points</td>
              <td>
                {formatStat(customerData.Cust_TotalSpentLoyaltyPoints, (v) =>
                  floorLoyaltyPoints(v)
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div className="customer-page">
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/customer/manageCustomer">Manage Customers</Link>
            </li>
            <li className="breadcrumb-item active">Update Customer</li>
          </ol>
          <h1 className="page-header mb-0">
            Update Customer
            {customerDisplayName ? (
              <span className="text-muted fw-normal" style={{ fontSize: "1.1rem" }}>
                {" "}
                — {customerDisplayName}
              </span>
            ) : null}
          </h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/customer/manageCustomer")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
          disabled={initialLoading || activeTab !== "general"}
        />
      </div>

      <GridSectionLoader loading={loading && !initialLoading} minHeight="240px">
        <div className="card border-0">
          {!initialLoading && (
            <ul
              className="nav nav-tabs customer-tabs mx-3 mt-3"
              role="tablist"
              aria-label="Customer sections"
            >
              <li className="nav-item">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "general"}
                  className={`nav-link ${activeTab === "general" ? "active" : ""}`}
                  style={
                    activeTab === "general"
                      ? { borderBottomColor: tabUnderline, color: tabUnderline }
                      : undefined
                  }
                  onClick={() => setActiveTab("general")}
                >
                  General
                </button>
              </li>
              {showLoyaltyTab && (
                <li className="nav-item">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "loyaltyHistory"}
                    className={`nav-link ${
                      activeTab === "loyaltyHistory" ? "active" : ""
                    }`}
                    style={
                      activeTab === "loyaltyHistory"
                        ? {
                            borderBottomColor: tabUnderline,
                            color: tabUnderline,
                          }
                        : undefined
                    }
                    onClick={() => setActiveTab("loyaltyHistory")}
                  >
                    Loyalty History
                  </button>
                </li>
              )}
              <li className="nav-item">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "orders"}
                  className={`nav-link ${activeTab === "orders" ? "active" : ""}`}
                  style={
                    activeTab === "orders"
                      ? { borderBottomColor: tabUnderline, color: tabUnderline }
                      : undefined
                  }
                  onClick={() => setActiveTab("orders")}
                >
                  Orders
                </button>
              </li>
            </ul>
          )}

          <Panel className="m-3 mt-0">
            <PanelBody>
              {initialLoading ? (
                <UpdateShimmer />
              ) : activeTab === "orders" ? (
                <>
                  <h6 className="customer-section-heading" style={sectionStyle}>
                    Orders
                  </h6>
                  {customerOrdersLoading ? (
                    <div className="text-muted py-3">Loading orders...</div>
                  ) : customerOrders.length === 0 ? (
                    <div className="text-muted py-3">
                      No orders found for this customer.
                    </div>
                  ) : (
                    <div className="table-responsive customer-data-table-wrap">
                      <table className="table table-hover align-middle mb-0 customer-data-table">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Date &amp; Time</th>
                            <th>Type</th>
                            <th>Payment</th>
                            <th>Status</th>
                            <th className="text-end">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerOrders.map((order, index) => (
                            <tr
                              key={
                                order?.recvorder_ID ||
                                order?.Recvorder_ID ||
                                `${index}-order`
                              }
                            >
                              <td>
                                {order?.recvorder_InvoiceNo ||
                                  order?.Recvorder_InvoiceNo ||
                                  order?.recvorder_FrontOrderID ||
                                  order?.Recvorder_FrontOrderID ||
                                  "—"}
                              </td>
                              <td>{getOrderDate(order)}</td>
                              <td>
                                {order?.recvorder_Ordertype ||
                                  order?.Recvorder_Ordertype ||
                                  order?.orderType ||
                                  "—"}
                              </td>
                              <td>{getPaymentStatusLabel(order)}</td>
                              <td>{getOrderStatusLabel(order)}</td>
                              <td className="text-end fw-semibold">
                                {formatCurrencyAmount(
                                  order?.recvorder_Totalamount ||
                                    order?.Recvorder_Totalamount ||
                                    order?.totalAmount ||
                                    0
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : activeTab === "loyaltyHistory" && showLoyaltyTab ? (
                <>
                  <div className="loyalty-summary-inline">
                    {loyaltySummaryCards.map((card) => (
                      <div key={card.key}>
                        <span className="label">{card.label}</span>
                        <span
                          className={`value ${card.valueClassName || ""}`}
                        >
                          {card.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <h6 className="customer-section-heading" style={sectionStyle}>
                    Transaction History
                  </h6>
                  {loyaltyHistoryLoading ? (
                    <div className="text-muted py-3">
                      Loading loyalty history...
                    </div>
                  ) : sortedLoyaltyTransactions.length === 0 ? (
                    <div className="text-muted py-3">
                      No loyalty history found for this customer.
                    </div>
                  ) : (
                    <div className="table-responsive customer-data-table-wrap">
                      <table className="table table-hover align-middle mb-0 customer-data-table">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Date &amp; Time</th>
                            <th>Type</th>
                            <th>Reference</th>
                            <th className="text-end">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedLoyaltyTransactions.map(
                            (transaction, index) => (
                              <tr
                                key={
                                  transaction?.history_ID ||
                                  transaction?.History_ID ||
                                  `${index}-${
                                    transaction?.order_ID ||
                                    transaction?.Order_ID ||
                                    "row"
                                  }`
                                }
                              >
                                <td>
                                  {transaction?.order_ID ||
                                    transaction?.Order_ID ||
                                    "—"}
                                </td>
                                <td>{getTransactionDate(transaction)}</td>
                                <td>
                                  <span
                                    className={`loyalty-type-badge type-${String(
                                      getTransactionType(transaction)
                                    ).toLowerCase()}`}
                                  >
                                    {getTransactionType(transaction)}
                                  </span>
                                </td>
                                <td>
                                  {transaction?.reference ||
                                    transaction?.Reference ||
                                    "—"}
                                </td>
                                <td
                                  className={`text-end fw-semibold ${
                                    getPointsLabel(transaction).startsWith("+")
                                      ? "points-positive"
                                      : getPointsLabel(transaction).startsWith(
                                          "-"
                                        )
                                      ? "points-negative"
                                      : ""
                                  }`}
                                >
                                  {getPointsLabel(transaction)}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                renderGeneralForm()
              )}
            </PanelBody>
          </Panel>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default UpdateCustomer;
